import {
  Box,
  HStack,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { gridConfig } from '@time-line/components/common';
import { Close } from '@time-line/components/icons';
import { BannerCardContent } from 'components/common/BannerCard/BannerCardContent';
import { useAnalytics } from 'hooks/useAnalytics';
import { useBannerCard } from 'hooks/useBannerCard';
import { Callout as CalloutConfig } from 'hooks/userPreferences/configs';
import { useLocalStorage } from 'hooks/userPreferences/useLocalStorage';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

const { borderWidthClamp } = gridConfig;

interface CalloutProps {
  targetRef: RefObject<any>;
  verticalOffset?: number;
  onCalloutClose?: () => void;
}

export function Callout({
  targetRef,
  verticalOffset = 0,
  onCalloutClose,
}: CalloutProps) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { analytics } = useAnalytics();
  const { data: bannerCard } = useBannerCard();

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const clonedHostRef = useRef<HTMLDivElement>(null);
  const originalNodeRef = useRef<HTMLElement | null>(null);
  const clonedNodeRef = useRef<HTMLElement | null>(null);

  const { setStoredValue: setCalloutViewedData } =
    useLocalStorage(CalloutConfig);

  useEffectOnce(() => {
    analytics.siteToggleCalloutDisplay();
    onOpen();
  });

  const handleClose = (action: 'dismiss' | 'onward') => {
    if (action === 'dismiss') {
      analytics.siteToggleCalloutClose();
    }
    if (action === 'onward') {
      analytics.siteToggleCalloutClick();
    }

    onClose();
    onCalloutClose?.();
    setCalloutViewedData(true);
  };

  useEffect(() => {
    const originalNode = targetRef?.current as HTMLElement | null;
    if (!originalNode) return;

    originalNodeRef.current = originalNode;

    setTargetRect(originalNode.getBoundingClientRect());

    const resizeObserver = new ResizeObserver(() => {
      setTargetRect(originalNode.getBoundingClientRect());
    });
    resizeObserver.observe(originalNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [targetRef]);

  // Attach clone when host exists (after render with targetRect)
  useEffect(() => {
    const host = clonedHostRef.current;
    const original = originalNodeRef.current;
    if (!host || !original) {
      return;
    }
    host.innerHTML = '';
    const freshClone = original.cloneNode(true) as HTMLElement;
    freshClone.style.pointerEvents = 'none';
    // The clone is a copy of a real button, so without this the dialog opens
    // onto a tabbable decoy that duplicates the original's pressed state.
    freshClone.inert = true;
    clonedNodeRef.current = freshClone;
    host.appendChild(freshClone);

    return () => {
      const currentClone = clonedNodeRef.current;
      if (currentClone && host.contains(currentClone)) {
        host.removeChild(currentClone);
      }
    };
  }, [targetRect]);

  // Recalculate rect on viewport resize
  useEffect(() => {
    const original = originalNodeRef.current;
    if (!original) return;

    let rafId = 0;
    const updateRect = () => {
      rafId = requestAnimationFrame(() => {
        const nextRect = original.getBoundingClientRect();
        setTargetRect(nextRect);
      });
    };

    updateRect();
    window.addEventListener('resize', updateRect);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetRef, isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={() => handleClose('dismiss')}>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent aria-label="Skincare store">
        {targetRect && bannerCard && (
          <Box
            position="fixed"
            top={`${targetRect.top}px`}
            left={`${targetRect.left}px`}
            width={`${targetRect.width}px`}
            height={`${targetRect.height}px`}
          >
            <Box
              ref={clonedHostRef}
              // Forward click to the original element
              onClick={() => {
                const originalNode = originalNodeRef.current;
                handleClose('onward');
                originalNode?.click();
              }}
              cursor="pointer"
              width="100%"
              height="100%"
            />
            <Box
              as="span"
              aria-hidden
              width="20px"
              height="20px"
              background="gray.50"
              borderRadius="sm"
              position="absolute"
              top={`${targetRect.height + 10}px`}
              left="50%"
              transform="translateX(-50%) rotate(45deg)"
            />
            <Box
              pos={{ base: 'fixed', lg: 'absolute' }}
              top={{
                base: `${targetRect.height + 30 + verticalOffset}px`,
                lg: `${targetRect.height + 19}px`,
              }}
              left={{ base: borderWidthClamp, sm: 'auto', lg: '50%' }}
              right={{ base: borderWidthClamp, lg: 'auto' }}
              transform={{ base: 'translateX(0)', lg: 'translateX(-50%)' }}
            >
              <BannerCardContent
                borderRadius="lg"
                p="4xs"
                pb="sm"
                w={{ base: '100%', sm: '345px' }}
                maxW="100%"
                onCtaClick={() => handleClose('onward')}
                card={bannerCard}
                header={
                  <HStack mb="2xs" justify="end">
                    <IconButton
                      pos="relative"
                      zIndex={1}
                      boxSize="lg"
                      variant="unstyled"
                      aria-label="Close"
                      icon={<Close boxSize="sm" />}
                      onClick={() => handleClose('dismiss')}
                    />
                  </HStack>
                }
              />
            </Box>
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
}
